import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { publicAPI, privateAPI } from '../services/api'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorMessage from '../components/ErrorMessage'
import PostnummerFilter from '../components/PostnummerFilter'
import DateFilterBar, { OutputMode, getDateRangeForMode } from '../components/DateFilterBar'
import { useYearDates } from '../contexts/YearContext'
import { Truck, PieChart, Building2, TrendingUp } from 'lucide-react'
import PeriodInfoBanner from '../components/PeriodInfoBanner'
import { useMaxDataDate } from '../hooks/useMaxDataDate'
import { usePersistedState } from '../hooks/usePersistedState'
import { useMinePostnummer } from '../hooks/useMinePostnummer'
import { localizeApiDetail } from '../i18n/apiError'

// Helper function to safely parse numeric values
const toNumber = (value: number | string | null | undefined): number => {
  if (value === null || value === undefined) return 0
  return typeof value === 'string' ? parseFloat(value) : value
}

interface BrandData {
  brand: string
  total_count: number
  market_share_percentage: number
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
}

interface MasterModelData {
  brand: string
  master_model: string
  total_count: number
  market_share_percentage: number
  ev_count: number
  ev_percentage: number
  leasing_count: number
  leasing_percentage: number
}

/* Commented out - incorrect data for commercial vehicles
interface LoanData {
  brand: string
  total_registrations: number
  vehicles_with_loan: number
  loan_percentage: number
}
*/

interface LeasingData {
  brand: string
  total_count: number
  leasing_count: number | string
  leasing_percentage: number | string
  private_leasing_count: number | string
  private_leasing_percentage: number | string
  company_leasing_count?: number | string
  company_leasing_percentage?: number | string
}

interface OrganizationClassification {
  classification: string
  count: number
  percentage: number
}

export default function VarebilAnalytics() {
  const { t, i18n } = useTranslation('leasingLoans')
  // Period label for the banner; mirrors DateFilterBar's OUTPUT_MODE_LABELS.
  // The persisted mode can be one DateFilterBar doesn't know (e.g. last_week from Aldersanalyse).
  const periodLabel = (mode: OutputMode) =>
    i18n.exists(`leasingLoans:outputModes.${mode}`) ? t(`outputModes.${mode}` as const) : t('periodFallback')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'brands' | 'models' | 'leasing' | 'organizations'>('brands')
  
  // Data states
  const [brandData, setBrandData] = useState<BrandData[]>([])
  const [modelData, setModelData] = useState<MasterModelData[]>([])
  // const [loanData, setLoanData] = useState<LoanData[]>([]) // Commented out - incorrect data
  const [leasingData, setLeasingData] = useState<LeasingData[]>([])
  const [orgClassificationData, setOrgClassificationData] = useState<OrganizationClassification[]>([])
  
  // Date range state from year context
  const { startDate, setStartDate, endDate, setEndDate, selectedYear, maxSvvDate } = useYearDates('varebil-analytics')
  
  // DateFilterBar state
  const [outputMode, setOutputMode] = usePersistedState<OutputMode>('bp:filter:outputMode', 'year_to_date')
  const [customStartDate, setCustomStartDate] = usePersistedState('bp:filter:customStartDate', '')
  const [customEndDate, setCustomEndDate] = usePersistedState('bp:filter:customEndDate', '')
  
  // Postal code filter state
  const [postnummerFilterEnabled, setPostnummerFilterEnabled] = usePersistedState('varebil-analytics:postnummerFilterEnabled', false)
  const minePostnummer = useMinePostnummer(postnummerFilterEnabled)
  
  // Date range validation
  const [dateRangeAlert, setDateRangeAlert] = useState<string | null>(null)

  // Max data date for banner
  const maxDataDate = useMaxDataDate()

  const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0]
  }

  // Validate date range (max 366 days)
  const validateDateRange = (start: Date, end: Date): boolean => {
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays > 366) {
      setDateRangeAlert(t('varebil.dateRangeTooLarge', { days: diffDays }))
      return false
    }
    
    setDateRangeAlert(null)
    return true
  }

  useEffect(() => {
    fetchData()
  }, [selectedYear, postnummerFilterEnabled, minePostnummer, activeTab])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const params: any = {
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        varebil: true,
      }

      if (postnummerFilterEnabled && minePostnummer) {
        params.mine_postnummer = minePostnummer
      }

      if (activeTab === 'brands') {
        const response = await publicAPI.getTopBrands({ ...params, limit: 50 })
        setBrandData(response.data.data || [])
      } else if (activeTab === 'models') {
        const response = await publicAPI.getMasterModels({ ...params, limit: 50 })
        setModelData(response.data.data || [])
      } /* else if (activeTab === 'loans') {
        // Commented out - shows incorrect data for commercial vehicles
        const response = await publicAPI.getLoansByBrand({ ...params, limit: 50 })
        setLoanData(response.data.data || [])
      } */ else if (activeTab === 'leasing') {
        const response = await privateAPI.getLeasingByBrand({ ...params, limit: 50 })
        setLeasingData(response.data.data || [])
      } else if (activeTab === 'organizations') {
        // Use the new organization classifications endpoint
        const response = await publicAPI.getOrganizationClassifications(params)
        setOrgClassificationData(response.data.data || [])
      }
    } catch (err: any) {
      console.error('Varebil analytics error:', err)
      setError(localizeApiDetail(err.response?.data?.detail) || err.message || t('varebil.loadError'))
    } finally {
      setLoading(false)
    }
  }

  const handleApplyFilter = () => {
    if (outputMode === 'custom') {
      if (customStartDate && customEndDate) {
        const s = new Date(customStartDate)
        const e = new Date(customEndDate)
        if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && validateDateRange(s, e)) {
          setStartDate(s)
          setEndDate(e)
          setTimeout(() => fetchData(), 0)
        }
      }
    } else {
      const { startDate: sd, endDate: ed } = getDateRangeForMode(outputMode, maxSvvDate ?? undefined)
      setStartDate(new Date(sd))
      setEndDate(new Date(ed))
      setDateRangeAlert(null)
      setTimeout(() => fetchData(), 0)
    }
  }

  const handleOutputModeChange = (mode: OutputMode) => {
    setOutputMode(mode)
    if (mode !== 'custom') {
      const { startDate: sd, endDate: ed } = getDateRangeForMode(mode, maxSvvDate ?? undefined)
      setStartDate(new Date(sd))
      setEndDate(new Date(ed))
      setDateRangeAlert(null)
      setTimeout(() => fetchData(), 0)
    }
  }


  if (error) {
    return <ErrorMessage message={error} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Truck className="w-8 h-8 text-primary-600 dark:text-primary-400" />
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
          {t('varebil.title')}
        </h1>
      </div>



      {/* Date Range Filter */}
      <DateFilterBar
        outputMode={outputMode}
        onOutputModeChange={handleOutputModeChange}
        customStartDate={customStartDate}
        onCustomStartDateChange={setCustomStartDate}
        customEndDate={customEndDate}
        onCustomEndDateChange={setCustomEndDate}
        onApply={handleApplyFilter}
        bottomContent={
          <PostnummerFilter
            enabled={postnummerFilterEnabled}
            onToggle={setPostnummerFilterEnabled}
            compact
          />
        }
      />

      {/* Date Range Alert */}
      {dateRangeAlert && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <p className="text-sm text-red-800 dark:text-red-200">
            <strong>{t('varebil.errorPrefix')}</strong> {dateRangeAlert}
          </p>
        </div>
      )}

      {/* Period Info Banner */}
      <PeriodInfoBanner
        maxDataDate={maxDataDate}
        outputMode={outputMode}
        periodLabel={periodLabel(outputMode)}
        startDate={startDate.toISOString().split('T')[0]}
        endDate={endDate.toISOString().split('T')[0]}
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-dark-border overflow-x-auto">
        <button
          onClick={() => setActiveTab('brands')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'brands'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            {t('varebil.tabs.brands')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('models')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'models'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Truck className="w-4 h-4" />
            {t('varebil.tabs.models')}
          </span>
        </button>
        {/* Commented out - shows incorrect data for commercial vehicles
        <button
          onClick={() => setActiveTab('loans')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'loans'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Låneanalyse
          </span>
        </button>
        */}
        <button
          onClick={() => setActiveTab('leasing')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'leasing'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4" />
            {t('varebil.tabs.leasing')}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('organizations')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap ${
            activeTab === 'organizations'
              ? 'border-primary-600 text-primary-600 dark:border-primary-400 dark:text-primary-400'
              : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
          }`}
        >
          <span className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            {t('varebil.tabs.organizations')}
          </span>
        </button>
      </div>

      {loading ? (
        <div className="card p-8 text-center">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {/* Brands Tab */}
          {activeTab === 'brands' && (
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center w-12">#</th>
                    <th>{t('varebil.cols.brand')}</th>
                    <th className="text-right">{t('varebil.cols.registrations')}</th>
                    <th className="text-right">{t('varebil.cols.evs')}</th>
                    <th className="text-right">{t('varebil.cols.leasingShare')}</th>
                  </tr>
                </thead>
                <tbody>
                  {brandData.map((brand, index) => (
                    <tr key={`${brand.brand}-${index}`}>
                      <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                      <td className="font-semibold">{brand.brand}</td>
                      <td className="text-right">{brand.total_count.toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        {toNumber(brand.ev_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({brand.ev_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(brand.leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({brand.leasing_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Models Tab */}
          {activeTab === 'models' && (
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center w-12">#</th>
                    <th>{t('varebil.cols.brand')}</th>
                    <th>{t('varebil.cols.masterModel')}</th>
                    <th className="text-right">{t('varebil.cols.registrations')}</th>
                    <th className="text-right">{t('varebil.cols.evs')}</th>
                    <th className="text-right">{t('varebil.cols.leasingShare')}</th>
                  </tr>
                </thead>
                <tbody>
                  {modelData.map((model, index) => (
                    <tr key={`${model.brand}-${model.master_model}-${index}`}>
                      <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                      <td className="font-semibold">{model.brand}</td>
                      <td>{model.master_model}</td>
                      <td className="text-right">{model.total_count.toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        {toNumber(model.ev_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({model.ev_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(model.leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({model.leasing_count.toLocaleString('nb-NO')})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Loans Tab - Commented out, shows incorrect data for commercial vehicles
          {activeTab === 'loans' && (
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th>Merke</th>
                    <th className="text-right">Registreringer</th>
                    <th className="text-right">Med lån</th>
                    <th className="text-right">Låneandel</th>
                  </tr>
                </thead>
                <tbody>
                  {loanData.map((loan, index) => (
                    <tr key={`${loan.brand}-${index}`}>
                      <td className="font-semibold">{loan.brand}</td>
                      <td className="text-right">{loan.total_registrations.toLocaleString('nb-NO')}</td>
                      <td className="text-right">{loan.vehicles_with_loan.toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        <span
                          className={`font-semibold ${
                            toNumber(loan.loan_percentage) > 70
                              ? 'text-red-600 dark:text-red-400'
                              : toNumber(loan.loan_percentage) > 40
                              ? 'text-orange-600 dark:text-orange-400'
                              : 'text-green-600 dark:text-green-400'
                          }`}
                        >
                          {toNumber(loan.loan_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          */}

          {/* Leasing Tab */}
          {activeTab === 'leasing' && (
            <div className="card overflow-x-auto">
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center w-12">#</th>
                    <th>{t('varebil.cols.brand')}</th>
                    <th className="text-right">{t('varebil.cols.registrations')}</th>
                    <th className="text-right">{t('varebil.cols.leased')}</th>
                    <th className="text-right">{t('varebil.cols.privatePersons')}</th>
                    <th className="text-right">{t('varebil.cols.companyLeasing')}</th>
                  </tr>
                </thead>
                <tbody>
                  {leasingData.map((leasing, index) => (
                    <tr key={`${leasing.brand}-${index}`}>
                      <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                      <td className="font-semibold">{leasing.brand}</td>
                      <td className="text-right">{toNumber(leasing.total_count).toLocaleString('nb-NO')}</td>
                      <td className="text-right">
                        {toNumber(leasing.leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(leasing.leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(leasing.private_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(leasing.private_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                      <td className="text-right">
                        {toNumber(leasing.company_leasing_percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %
                        <span className="text-xs text-gray-500 ml-1">
                          ({toNumber(leasing.company_leasing_count).toLocaleString('nb-NO')})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && (
            <div className="card overflow-x-auto">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4 p-6 pb-0">
                {t('varebil.orgTitle')}
              </h2>
              <table className="data-table data-table-compact">
                <thead>
                  <tr>
                    <th className="text-center w-12">#</th>
                    <th>{t('varebil.cols.classification')}</th>
                    <th className="text-right">{t('varebil.cols.vanCount')}</th>
                    <th className="text-right">{t('varebil.cols.share')}</th>
                  </tr>
                </thead>
                <tbody>
                  {orgClassificationData.map((org, index) => (
                    <tr key={`${org.classification}-${index}`}>
                      <td className="text-center text-gray-700 dark:text-gray-300">{index + 1}</td>
                      <td className="font-semibold capitalize">{org.classification}</td>
                      <td className="text-right">
                        {toNumber(org.count).toLocaleString('nb-NO')}
                        <span className="text-gray-500 dark:text-gray-400 text-sm ml-2">
                          ({toNumber(org.percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %)
                        </span>
                      </td>
                      <td className="text-right">{toNumber(org.percentage).toLocaleString('nb-NO', {minimumFractionDigits:2, maximumFractionDigits:2})} %</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
